import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { DollarSign, Heart, Loader2, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';

const TIP_AMOUNTS = [5, 10, 20, 50, 100];

export function TipButton({ creatorId, creatorName }) {
  const { api, isAuthenticated } = useAuth();
  const [open, setOpen] = useState(false);
  const [selectedAmount, setSelectedAmount] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleTip = async () => {
    if (!selectedAmount) {
      toast.error('Choisis un montant');
      return;
    }

    if (!isAuthenticated) {
      toast.error('Connecte-toi pour envoyer un pourboire');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/payments/tip/checkout', {
        creator_id: creatorId,
        amount: selectedAmount,
        origin_url: window.location.origin
      });

      if (response.data?.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors du paiement');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button 
          className="bg-gradient-to-r from-[#D9381E] to-[#E07A5F] hover:from-[#c42f17] hover:to-[#d16b50] text-white rounded-full shadow-lg"
          data-testid="tip-button"
        >
          <Heart className="w-4 h-4 mr-2" />
          Envoyer un tip
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md rounded-3xl">
        <DialogHeader>
          <DialogTitle className="font-fraunces text-xl text-center">
            Soutenir {creatorName}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          {/* Creator gets message */}
          <div className="text-center text-sm text-gray-500">
            <Heart className="w-6 h-6 text-[#D9381E] mx-auto mb-2" />
            80% du montant va directement au createur
          </div>

          {/* Amount selection */}
          <div className="grid grid-cols-5 gap-2">
            {TIP_AMOUNTS.map((amount) => (
              <button
                key={amount}
                onClick={() => setSelectedAmount(amount)}
                className={`py-3 px-2 rounded-xl font-bold text-sm transition-all ${
                  selectedAmount === amount
                    ? 'bg-[#1E4ED8] text-white shadow-lg scale-105'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {amount}
              </button>
            ))}
          </div>
          
          <p className="text-center text-sm text-gray-500">
            Montant en TND (Dinar Tunisien)
          </p>

          {/* Selected amount summary */}
          {selectedAmount && (
            <div className="bg-[#F8F5F2] rounded-xl p-4 text-center">
              <p className="text-sm text-gray-600 mb-1">Tu vas envoyer</p>
              <p className="font-fraunces text-3xl font-bold text-[#1E4ED8]">
                {selectedAmount} TND
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {creatorName} recevra {(selectedAmount * 0.8).toFixed(2)} TND
              </p>
            </div>
          )}

          {/* Pay button */}
          <Button
            onClick={handleTip}
            disabled={!selectedAmount || loading}
            className="w-full h-12 bg-gradient-to-r from-[#1E4ED8] to-[#60A5FA] hover:from-[#1E40AF] hover:to-[#3b82f6] text-white rounded-full text-lg"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <Loader2 className="w-5 h-5 animate-spin" />
                Redirection vers Stripe...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <DollarSign className="w-5 h-5" />
                Payer avec Stripe
              </span>
            )}
          </Button>

          <p className="text-center text-xs text-gray-400">
            Paiement securise par Stripe. Tu seras redirige vers la page de paiement.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
